import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { 
  Tags, Plus, Trash2, Zap, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function BankingRules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({
    pattern: '',
    category: '',
    is_income: false
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/banking/rules`);
      setRules(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load rules'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const response = await axios.post(`${API_URL}/banking/rules`, form);
      toast.success(`Rule created. Applied to ${response.data.applied_to} existing transactions.`);
      setShowDialog(false);
      setForm({ pattern: '', category: '', is_income: false });
      fetchRules();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create rule'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (rule) => {
    if (!confirm(`Delete rule "${rule.pattern}" → ${rule.category}?`)) return;

    try {
      await axios.delete(`${API_URL}/banking/rules/${rule.id}`);
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete rule'));
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="banking-rules">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Categories & Rules</h1>
            <p className="text-muted-foreground mt-1">Auto-categorize transactions based on description patterns</p>
          </div>
          <Button onClick={() => setShowDialog(true)} data-testid="add-rule-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Rule
          </Button>
        </div>

        {/* How it works */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-800 flex items-center gap-2">
              <Zap className="h-5 w-5" />
              How Auto-Categorization Works
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-blue-700 space-y-2">
            <p>
              When you import a bank statement, each transaction description is checked against your rules.
            </p>
            <p>
              <strong>Example:</strong> If you create a rule with pattern "ENGEN" and category "Fuel", 
              any transaction with "ENGEN" in the description will automatically be categorized as Fuel.
            </p>
            <p>
              Rules are case-insensitive and match anywhere in the description.
            </p>
          </CardContent>
        </Card>

        {/* Rules Table */}
        <Card>
          <CardHeader>
            <CardTitle>Active Rules</CardTitle>
            <CardDescription>{rules.length} rule{rules.length !== 1 ? 's' : ''} configured</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">Loading...</div>
            ) : rules.length === 0 ? (
              <div className="text-center py-12">
                <Tags className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">No rules yet</h3>
                <p className="text-muted-foreground mb-4">Create rules to auto-categorize transactions</p>
                <Button onClick={() => setShowDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Rule
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pattern (matches description)</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell className="font-mono text-sm">
                        {rule.pattern}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{rule.category}</Badge>
                      </TableCell>
                      <TableCell>
                        {rule.is_income ? (
                          <Badge className="bg-green-100 text-green-700">
                            <ArrowUpRight className="h-3 w-3 mr-1" />
                            Income
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700">
                            <ArrowDownRight className="h-3 w-3 mr-1" />
                            Expense
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(rule)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Suggested Rules */}
        <Card>
          <CardHeader>
            <CardTitle>Common Patterns</CardTitle>
            <CardDescription>Click to quickly add common categorization rules</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {[
                { pattern: 'ENGEN', category: 'Fuel' },
                { pattern: 'SHELL', category: 'Fuel' },
                { pattern: 'SASOL', category: 'Fuel' },
                { pattern: 'CALTEX', category: 'Fuel' },
                { pattern: 'TOLL', category: 'Toll Fees' },
                { pattern: 'N1TOLL', category: 'Toll Fees' },
                { pattern: 'SANRAL', category: 'Toll Fees' },
                { pattern: 'INSURANCE', category: 'Insurance' },
                { pattern: 'SALARY', category: 'Salary', is_income: false },
                { pattern: 'PAYMENT RECEIVED', category: 'Income', is_income: true },
              ].map((suggestion, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setForm({
                      pattern: suggestion.pattern,
                      category: suggestion.category,
                      is_income: suggestion.is_income || false
                    });
                    setShowDialog(true);
                  }}
                >
                  {suggestion.pattern} → {suggestion.category}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Add Rule Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Category Rule</DialogTitle>
              <DialogDescription>
                Create a rule to auto-categorize transactions based on description
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="pattern">Pattern *</Label>
                  <Input
                    id="pattern"
                    value={form.pattern}
                    onChange={(e) => setForm({ ...form, pattern: e.target.value })}
                    placeholder="e.g., ENGEN, SHELL, TOLL"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Text to match in transaction description (case-insensitive)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Input
                    id="category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    placeholder="e.g., Fuel, Toll Fees, Insurance"
                    required
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label>Is this income?</Label>
                    <p className="text-sm text-muted-foreground">
                      Enable if this pattern matches income (credits)
                    </p>
                  </div>
                  <Switch
                    checked={form.is_income}
                    onCheckedChange={(checked) => setForm({ ...form, is_income: checked })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading}>
                  {formLoading ? 'Creating...' : 'Create Rule'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
